import { registerLocaleData as registerAngularLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import angularRuLocale from '@angular/common/locales/ru';
import { ApplicationConfig, DEFAULT_CURRENCY_CODE, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@app/environment';
import { provideStore } from '@ngxs/store';
import { provideAngularSvgIcon, SvgLoader } from 'angular-svg-icon';
import { ru as dateFnsRu } from 'date-fns/locale';
import { setDefaultOptions as setDateFnsDefaultOptions } from 'date-fns/setDefaultOptions';

import { routes } from './app.routes';
import { apiBaseUrlInterceptor } from './core/api';
import { provideAuth, unauthorizedInterceptor } from './core/auth';
import { loaderHttpErrorInterceptor } from './core/loader/loader-http-error.interceptor';
import { provideMediaObserver } from './core/media-observer/media-observer.provider';
import { provideNavigation } from './core/navigation/navigation.module';
import { provideSidenav } from './core/sidenav/sidenav.module';
import { svgBrowserLoaderFactory } from './core/svg-icon/svg-browser-loader.service';
import { APP_SVG_ICON_PATH } from './core/tokens/app-svg-icon-path.token';
import { provideUpdater } from './core/updater/updater.module';
import { provideGoalCounters } from './features/goal/goal-counters-state/goal-counters-state.provider';
import { provideGoalStatusFlow } from './features/goal/goal-status-flow';
import { provideNewsComplaints } from './features/news/news-complaint-state/news-complaint-state.module';

registerAngularLocaleData(angularRuLocale);
setDateFnsDefaultOptions({ locale: dateFnsRu });

const MAT_FORM_FIELD_OPTIONS: MatFormFieldDefaultOptions = {
  appearance: 'outline',
};

export const appConfig: ApplicationConfig = {
  providers: [
    /* Tokens */
    { provide: LOCALE_ID, useValue: 'ru' },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'RUB',
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: dateFnsRu,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: MAT_FORM_FIELD_OPTIONS,
    },
    {
      provide: APP_SVG_ICON_PATH,
      useValue: '/assets/icons/svg',
    },
    provideStore([], {
      developmentMode: !environment.production,
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !environment.isLocal,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideDateFnsAdapter(),
    provideAngularSvgIcon({
      loader: {
        provide: SvgLoader,
        useFactory: svgBrowserLoaderFactory,
        deps: [HttpClient, APP_SVG_ICON_PATH],
      },
    }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([apiBaseUrlInterceptor, unauthorizedInterceptor, loaderHttpErrorInterceptor])),
    provideAnimations(),
    provideZoneChangeDetection(),

    /** Core */
    provideAuth(),
    provideMediaObserver({
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1400,
      xxxl: 1600,
    }),
    provideNavigation(),
    provideSidenav(),
    provideUpdater(),

    /** Features */
    provideGoalCounters(),
    provideGoalStatusFlow(),
    provideNewsComplaints(),
  ],
};
